import type { AnalyticsService, AnalyticsUser } from './types';
import { analiticaActivaEnNavegador } from './entorno';

// Lazy-load posthog-js to avoid blocking the main thread on initial render.
// The library (~200KB) is only imported when the first analytics call is made.
let _posthog: typeof import('posthog-js').default | null = null;
let _posthogPromise: Promise<typeof import('posthog-js').default> | null = null;

function getPostHog(): typeof import('posthog-js').default | null {
  return _posthog;
}

function ensurePostHog(): Promise<typeof import('posthog-js').default> {
  if (_posthog) return Promise.resolve(_posthog);
  if (_posthogPromise) return _posthogPromise;

  _posthogPromise = import('posthog-js').then((mod) => {
    _posthog = mod.default;
    return _posthog;
  });

  return _posthogPromise;
}

// Fire-and-forget helper: queue the call until posthog is loaded
function withPostHog(fn: (ph: typeof import('posthog-js').default) => void): void {
  // Fuera de producción no se carga posthog-js ni se manda nada.
  if (!analiticaActivaEnNavegador()) return;
  const ph = getPostHog();
  if (ph) {
    fn(ph);
  } else {
    ensurePostHog().then(fn);
  }
}

class PostHogClientService implements AnalyticsService {
  // ---- IdentityService ----
  identify(userId: string, properties?: Record<string, unknown>): void {
    withPostHog((ph) => ph.identify(userId, properties));
  }

  group(
    groupType: string,
    groupId: string,
    properties?: Record<string, unknown>
  ): void {
    withPostHog((ph) => ph.group(groupType, groupId, properties));
  }

  reset(): void {
    withPostHog((ph) => ph.reset());
  }

  register(superProperties: Record<string, unknown>): void {
    withPostHog((ph) => ph.register(superProperties));
  }

  // ---- EventService ----
  capture(eventName: string, properties?: Record<string, unknown>): void {
    withPostHog((ph) => ph.capture(eventName, properties));
  }

  captureException(
    error: Error,
    properties?: Record<string, unknown>
  ): void {
    withPostHog((ph) =>
      ph.capture('$exception', {
        $exception_message: error.message,
        $exception_type: error.name,
        $exception_stack_trace_raw: error.stack,
        ...properties,
      })
    );
  }

  // ---- FeatureFlagService ----
  isFeatureEnabled(flagKey: string, defaultValue = false): boolean {
    const ph = getPostHog();
    return ph ? (ph.isFeatureEnabled(flagKey) ?? defaultValue) : defaultValue;
  }

  getFeatureFlag(flagKey: string): boolean | string | undefined {
    const ph = getPostHog();
    return ph ? ph.getFeatureFlag(flagKey) : undefined;
  }

  onFeatureFlags(callback: () => void): void {
    withPostHog((ph) => ph.onFeatureFlags(callback));
  }

  // ---- ConsentService ----
  hasConsented(): boolean {
    const ph = getPostHog();
    return ph ? ph.has_opted_in_capturing() : false;
  }

  optIn(): void {
    withPostHog((ph) => ph.opt_in_capturing());
  }

  optOut(): void {
    withPostHog((ph) => ph.opt_out_capturing());
  }
}

// Drop keys whose value is undefined so we never overwrite an existing PostHog
// property with `undefined` (e.g. when identify runs at login before the plan
// has been fetched, then again on the dashboard with the full context).
function compact(
  props: Record<string, unknown>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(props).filter(([, v]) => v !== undefined)
  );
}

// Helper: identify with the full AnalyticsUser shape
export function identifyUser(
  analyticsService: AnalyticsService,
  user: AnalyticsUser
): void {
  const plan = user.plan ?? (user.hasSubscription ? undefined : 'free');

  analyticsService.identify(
    user.id,
    compact({
      email: user.email,
      name: [user.firstName, user.lastName].filter(Boolean).join(' ') || undefined,
      first_name: user.firstName,
      last_name: user.lastName,
      user_type: user.userType,
      role_id: user.userRoleId,
      timezone: user.timezone,
      email_verified: user.emailVerified,
      locale: user.locale,
      // Account / plan context — the fields that make billing & entitlement
      // issues (e.g. "can't create corpus on a free plan") obvious in PostHog.
      account_id: user.activeAccountId,
      account_name: user.accountName,
      account_role: user.accountRole,
      account_count: user.accountCount,
      plan,
      plan_id: user.planId,
      subscription_status: user.subscriptionStatus,
      has_subscription: user.hasSubscription,
    })
  );

  if (user.activeAccountId) {
    analyticsService.group(
      'company',
      user.activeAccountId,
      compact({
        name: user.accountName,
        plan,
        plan_id: user.planId,
        subscription_status: user.subscriptionStatus,
        has_subscription: user.hasSubscription,
      })
    );
  }

  // Super properties ride along on every captured event (incl. API_ERROR),
  // so errors can be sliced by plan/account without extra instrumentation.
  analyticsService.register(
    compact({
      user_type: user.userType,
      account_id: user.activeAccountId,
      plan,
    })
  );
}

// Singleton export
export const analytics: AnalyticsService = new PostHogClientService();
